import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api, formatDate, API_URL } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, Download, Plus, CheckCircle, CalendarIcon, FileText, ArrowLeft, DollarSign, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

export default function VolunteerHours() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hours, setHours] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [logType, setLogType] = useState('volunteer');
  const [date, setDate] = useState(new Date());
  const [form, setForm] = useState({ opp_title: '', hours: '', description: '', earnings: '' });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('volunteer');

  useEffect(() => {
    loadHours();
  }, []);

  const loadHours = async () => {
    try {
      const res = await api.get('/hours');
      setHours(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleLog = async () => {
    if (!form.opp_title || !form.hours || !form.description) {
      toast.error('Please fill in all fields');
      return;
    }
    if (logType === 'sidegig' && !form.earnings) {
      toast.error('Please enter your earnings');
      return;
    }
    try {
      await api.post('/hours', {
        opp_title: form.opp_title,
        hours: parseFloat(form.hours),
        description: form.description,
        date: format(date, 'yyyy-MM-dd'),
        type: logType,
        earnings: logType === 'sidegig' ? parseFloat(form.earnings) : null,
      });
      toast.success(logType === 'volunteer' ? 'Volunteer hours logged!' : 'Side gig logged!');
      setShowLog(false);
      setForm({ opp_title: '', hours: '', description: '', earnings: '' });
      loadHours();
    } catch {
      toast.error('Failed to log hours');
    }
  };

  const downloadReport = () => {
    window.open(`${API_URL}/hours/report`, '_blank');
  };

  const volHours = hours.filter(h => h.type !== 'sidegig');
  const gigHours = hours.filter(h => h.type === 'sidegig');
  const totalVol = volHours.reduce((s, h) => s + (h.hours || 0), 0);
  const verifiedVol = volHours.filter(h => h.verified).reduce((s, h) => s + (h.hours || 0), 0);
  const totalGigHrs = gigHours.reduce((s, h) => s + (h.hours || 0), 0);
  const totalEarnings = gigHours.reduce((s, h) => s + (h.earnings || 0), 0);

  const renderHoursList = (list, showEarnings = false) => {
    if (loading) return (
      [1, 2, 3].map(i => (
        <div key={i} className="bg-white rounded-2xl p-4 border-2 border-gray-100 animate-pulse">
          <div className="h-4 bg-gray-100 rounded w-1/2 mb-2" />
          <div className="h-3 bg-gray-100 rounded w-1/3" />
        </div>
      ))
    );
    if (list.length === 0) return (
      <div className="bg-white rounded-3xl p-8 border-2 border-gray-100 text-center">
        <FileText size={40} className="text-[#9AA5B1] mx-auto mb-3" />
        <p className="text-[#5C6B7F] font-semibold">
          {showEarnings ? 'No side gigs logged yet' : 'No volunteer hours logged yet'}
        </p>
        <p className="text-sm text-[#9AA5B1]">Tap the button above to start tracking!</p>
      </div>
    );
    return list.map(h => (
      <div key={h.hour_id} className="bg-white rounded-2xl p-4 border-2 border-gray-100 flex items-center gap-3" data-testid={`hour-${h.hour_id}`}>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${h.verified ? 'bg-[#2EC4B6]/10' : 'bg-gray-100'}`}>
          {showEarnings ? <DollarSign size={20} className="text-[#FF9F1C]" /> :
           h.verified ? <CheckCircle size={20} className="text-[#2EC4B6]" /> : <Clock size={20} className="text-[#9AA5B1]" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#011627] truncate">{h.opp_title}</p>
          <p className="text-xs text-[#5C6B7F]">{formatDate(h.date)} &middot; {h.description}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-bold text-[#011627]">{h.hours}h</p>
          {showEarnings && h.earnings ? (
            <span className="text-xs font-bold text-[#FF9F1C]">${h.earnings.toFixed(2)}</span>
          ) : (
            <span className={`text-[10px] font-bold uppercase ${h.verified ? 'text-[#2EC4B6]' : 'text-[#9AA5B1]'}`}>
              {h.verified ? 'Verified' : 'Pending'}
            </span>
          )}
        </div>
      </div>
    ));
  };

  return (
    <div className="px-4 pt-6 max-w-lg mx-auto pb-8" data-testid="volunteer-hours-page">
      <SEO title="Activity Tracker" description="Track your volunteer hours and side gig earnings on TeenHands. Download PDF reports for school or college applications." path="/volunteer-hours" />
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C6B7F] font-bold mb-4" data-testid="back-button">
        <ArrowLeft size={20} /> Back
      </button>

      <h1 className="text-3xl font-bold text-[#011627] mb-4 animate-fade-in-up">Activity Tracker</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
        <div className="bg-[#2EC4B6] rounded-3xl p-5 text-white">
          <Clock size={22} className="mb-2" strokeWidth={2.5} />
          <p className="text-3xl font-bold">{totalVol.toFixed(1)}</p>
          <p className="text-sm font-bold opacity-80">Volunteer Hours</p>
          <p className="text-xs opacity-60">{verifiedVol.toFixed(1)} verified</p>
        </div>
        <div className="bg-[#FF9F1C] rounded-3xl p-5 text-white">
          <Briefcase size={22} className="mb-2" strokeWidth={2.5} />
          <p className="text-3xl font-bold">{totalGigHrs.toFixed(1)}</p>
          <p className="text-sm font-bold opacity-80">Side Gig Hours</p>
          <p className="text-xs opacity-60">${totalEarnings.toFixed(2)} earned</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 mb-6 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
        <Button
          onClick={() => { setLogType('volunteer'); setShowLog(true); }}
          className="flex-1 rounded-2xl py-4 bg-[#2EC4B6] hover:bg-[#25A296] text-white font-bold btn-bounce text-sm"
          data-testid="log-volunteer-btn"
        >
          <Plus size={16} className="mr-1" /> Volunteer
        </Button>
        <Button
          onClick={() => { setLogType('sidegig'); setShowLog(true); }}
          className="flex-1 rounded-2xl py-4 bg-[#FF9F1C] hover:bg-[#E58E19] text-white font-bold btn-bounce text-sm"
          data-testid="log-sidegig-btn"
        >
          <Plus size={16} className="mr-1" /> Side Gig
        </Button>
        <Button
          onClick={downloadReport}
          variant="outline"
          className="rounded-2xl py-4 border-2 font-bold btn-bounce px-4"
          data-testid="download-report-btn"
        >
          <Download size={16} />
        </Button>
      </div>

      {/* Tabs for Volunteer / Side Gigs */}
      <Tabs defaultValue="volunteer" value={activeTab} onValueChange={setActiveTab} className="animate-fade-in-up" style={{ animationDelay: '300ms' }}>
        <TabsList className="w-full bg-gray-100 rounded-2xl p-1 mb-4">
          <TabsTrigger value="volunteer" className="flex-1 rounded-xl font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-volunteer">
            Volunteer ({volHours.length})
          </TabsTrigger>
          <TabsTrigger value="sidegig" className="flex-1 rounded-xl font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-sidegig">
            Side Gigs ({gigHours.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="volunteer">
          <div className="space-y-3">{renderHoursList(volHours, false)}</div>
        </TabsContent>
        <TabsContent value="sidegig">
          <div className="space-y-3">{renderHoursList(gigHours, true)}</div>
        </TabsContent>
      </Tabs>

      {/* Log Dialog */}
      <Dialog open={showLog} onOpenChange={setShowLog}>
        <DialogContent className="rounded-3xl border-2 border-gray-100 max-w-md" data-testid="log-hours-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {logType === 'volunteer' ? 'Log Volunteer Hours' : 'Log Side Gig'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              placeholder={logType === 'volunteer' ? 'Activity/Organization Name *' : 'Gig Name *'}
              value={form.opp_title} onChange={e => setForm({ ...form, opp_title: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="log-activity"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number" placeholder="Hours *" min={0.5} step={0.5}
                value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })}
                className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
                data-testid="log-hours-input"
              />
              {logType === 'sidegig' && (
                <Input
                  type="number" placeholder="Earnings ($) *" min={0} step={0.01}
                  value={form.earnings} onChange={e => setForm({ ...form, earnings: e.target.value })}
                  className="bg-gray-50 border-2 border-gray-100 focus:border-[#FF9F1C] rounded-2xl px-4 py-3 font-semibold"
                  data-testid="log-earnings-input"
                />
              )}
            </div>
            <Textarea
              placeholder="What did you do? *"
              value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl font-semibold"
              data-testid="log-description"
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full rounded-2xl py-3 border-2 border-gray-100 justify-start font-semibold"
                  data-testid="log-date-picker"
                >
                  <CalendarIcon size={16} className="mr-2 text-[#2EC4B6]" />
                  {format(date, 'PPP')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
                <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} initialFocus />
              </PopoverContent>
            </Popover>
            <Button
              onClick={handleLog}
              className={`w-full rounded-full py-5 text-white font-bold text-lg btn-bounce ${
                logType === 'volunteer' ? 'bg-[#2EC4B6] hover:bg-[#25A296]' : 'bg-[#FF9F1C] hover:bg-[#E58E19]'
              }`}
              data-testid="submit-hours"
            >
              {logType === 'volunteer' ? 'Log Volunteer Hours' : 'Log Side Gig'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
